import React, { useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Button } from "react-bootstrap"
import PropTypes from "prop-types"

import { API_STATUS } from "../../../shared_components/widgets/utils"
import { deepcopy, showModal, hideModal, isNumber } from "../../../shared_components/utils"
import PageLoadingPanel from "../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../shared_components/widgets/PageErrorPanel"
import { withSampleConfig } from "../../../config/SampleConfigProvider"
import { MethodColumnSpanner } from "../resources/MethodColumnSpanner"
import ModalSamplePoint from "./ModalSamplePoint"
import { getFormattedMessage } from "../../shared_components_registration"
import { useSensePageWidth } from "./FormSamplePoint"

/* Component to manage Sample points and display Offline Schedule */
const SampleSchedule = ({
  allowSelection,
  allowActions,
  allowSampleCreation,
  sample_point_status,
  sample_point_delete_status,
  sample_points_selected,
  focus_sample_points = [],
  sample_point_data,
  getSamplePoints,
  deleteSamplePoint,
  updateSelectedSamplePoints,
  updateSamplesToCreate,
  user_permissions = [],
  largeScreenCutoff = 1250,
  isNSView,
}) => {
  const intl = useIntl()
  const [status_data, setStatusData] = useState(API_STATUS.LOADING)
  const [data, setData] = useState([])
  const [selectedRows, setSelectedRows] = useState({})
  const [selectedSamplePoint, setSelectedSamplePoint] = useState(null)
  const [showSamplePointModal, setShowSamplePointModal] = useState(false)

  const pageWidth = useSensePageWidth()

  useEffect(() => {
    getSamplePoints()
  }, [])

  useEffect(() => {
    if (focus_sample_points.length > 0) {
      focus_sample_points.forEach((sp) => {
        updateSelectedSamplePoints(sp.id, true)
      })
      setData(focus_sample_points)
    } else {
      setData(sample_point_data)
    }

    setStatusData(sample_point_status)
  }, [sample_point_status, sample_point_data, focus_sample_points])

  useEffect(() => {
    setSelectedRows(sample_points_selected)
  }, [sample_points_selected])

  useEffect(() => {
    if (sample_point_delete_status === API_STATUS.ERROR) {
      alert(intl.formatMessage({ id: "samplemanagement.unable.to.delete.sample.point" }))
    }
  }, [sample_point_delete_status])

  const onClickDeleteSamplePoint = (event, sample_point) => {
    if (
      !confirm(
        `${intl.formatMessage({
          id: "samplemanagement.confirm.delete.sample.point",
        })}:\n ${sample_point.sample_conn_no}`,
      )
    ) {
      return false
    }

    deleteSamplePoint(sample_point.id, {})
  }

  const tiHeaders = isNSView
    ? [{ id: "general.target.value", desc: "Target Value" }]
    : [
        { id: "samplemanagement.min.process.range", desc: "Min Process Range", large_only: true },
        { id: "samplemanagement.max.process.range", desc: "Max Process Range", large_only: true },
        { id: "samplemanagement.min.test.range", desc: "Min Test Range", large_only: true },
        { id: "samplemanagement.max.test.range", desc: "Max Test Range", large_only: true },
      ]

  const headers = [
    {
      id: "samplemanagement.sample.connection.number",
      desc: "Sample Connection Number",
    },
    {
      id: "samplemanagement.stream.name.and.pfd.stream.number",
      desc: "Stream Name and PFD Stream Numbers",
      style: { width: "100px" },
    },
    { id: "general.state", desc: "Gas, Liquid, or Solid", style: { minWidth: "80px" } },
    { id: "general.reactor", desc: "Reactor(s)", style: { width: "150px" } },
    {
      id: "general.tag",
      desc: "Tag Name",
      style: { minWidth: "80px" },
    },
    {
      id: "samplemanagement.test.item",
      desc: "Test Item",
      style: { minWidth: "80px" },
    },
    ...tiHeaders,
    { id: "general.units", desc: "Units" },
    { id: "samplemanagement.frequency", desc: "Frequency" },
    { id: "samplemanagement.sample.prefix", desc: "Sample Prefix" },
  ]

  const isLargeDisplay = pageWidth > largeScreenCutoff

  const methodSpannerParams = (i, name) => ({
    name: name,
    colSpan: isNSView ? 4 : isLargeDisplay ? 7 : 3,
    method_info: data?.[i]?.methods?.[name],
    onMethodClick: () => {
      let sp_data = [...data]
      let current_sp = { ...data[i] }
      current_sp["methods"][name]["open"] = !data[i]["methods"][name]["open"]
      sp_data[i] = current_sp
      setData(sp_data)
    },
  })

  return (
    <React.Fragment>
      {status_data === API_STATUS.LOADING && <PageLoadingPanel />}

      {status_data === API_STATUS.ERROR && <PageErrorPanel attemptReload={getSamplePoints} />}

      {status_data === API_STATUS.SUCCESS &&
        (data.length ? (
          <table className={"table table-bordered tbl_lanzatable table-sm"}>
            <thead>
              <tr>
                {allowActions ? (
                  <th style={{ minWidth: "76px" }}>&nbsp;</th>
                ) : allowSelection ? (
                  <th style={{ minWidth: "30px" }}>&nbsp;</th>
                ) : null}
                {allowSampleCreation && (
                  <th className={"text-center"} style={{ fontSize: "90%" }}>
                    <FormattedMessage
                      id={"samplemanagement.number.of.samples"}
                      description={"Sample # Table Column"}
                      defaultMessage={"Sample #"}
                    />
                  </th>
                )}
                {headers
                  .filter((header) => isLargeDisplay || (!isLargeDisplay && !header["large_only"]))
                  .map((header) => (
                    <th
                      className={"text-center"}
                      key={header.desc}
                      style={{ ...(header["style"] ? header["style"] : {}), fontSize: "90%" }}
                    >
                      {getFormattedMessage(header.id, header.desc)}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {data.map((sp, sp_idx) => {
                const first_method = Object.keys(sp.methods).length > 0 ? Object.keys(sp.methods)[0] : "-"
                const row_span = getRowSpanNum(sp)

                return (
                  <React.Fragment key={sp_idx}>
                    <tr>
                      {allowSelection ? (
                        <td className={"text-center"} rowSpan={row_span}>
                          <input
                            type={"checkbox"}
                            name={"selected_sample_points"}
                            defaultChecked={selectedRows[sp.id + ""]}
                            onChange={(e) => updateSelectedSamplePoints(sp.id, e.target.checked)}
                          />
                        </td>
                      ) : allowActions ? (
                        <td className={"text-center"} rowSpan={row_span}>
                          <Button
                            variant={"outline-primary"}
                            disabled={!user_permissions.includes("change_samplingpoint")}
                            className={"btn btn-sm"}
                            onClick={(_) => {
                              setSelectedSamplePoint(deepcopy(sp))
                              showModal(setShowSamplePointModal)
                            }}
                          >
                            <span className={"fa fa-pencil-square-o"} />
                          </Button>
                          &nbsp;
                          <Button
                            variant={"outline-primary"}
                            disabled={!user_permissions.includes("delete_samplingpoint")}
                            className={"btn btn-sm"}
                            onClick={(event) => onClickDeleteSamplePoint(event, sp)}
                          >
                            <span className={"fa fa-trash-o text-danger"} />
                          </Button>
                        </td>
                      ) : null}
                      {allowSampleCreation && (
                        <td className={"text-center"} rowSpan={row_span} style={{ fontSize: "90%" }}>
                          <input
                            type={"number"}
                            name={"number_of_samples"}
                            min={1}
                            value={
                              selectedRows[sp.id]?.checked ? selectedRows[sp.id]?.samples_to_create || 1 : 0
                            }
                            style={{ maxWidth: "50px", textAlign: "center" }}
                            onChange={(e) => updateSamplesToCreate(sp.id, e.target.value)}
                            disabled={!selectedRows[sp.id]?.checked}
                          />
                        </td>
                      )}
                      <td rowSpan={row_span} className={"text-center"} style={{ fontSize: "90%" }}>
                        {sp["sample_conn_no"]}
                      </td>
                      <td rowSpan={row_span} className={"text-center"} style={{ fontSize: "90%" }}>
                        {sp["stream_name"]} ({sp["pfd_stream_no"] ? sp["pfd_stream_no"] : "-"})
                      </td>
                      <td rowSpan={row_span} className={"text-center"} style={{ fontSize: "90%" }}>
                        {sp["sample_state"]}
                      </td>
                      <td rowSpan={row_span} className={"text-center"} style={{ fontSize: "90%" }}>
                        {sp["reactor_list"].map((reactor) => reactor.name).join(", ")}
                      </td>
                      <MethodColumnSpanner {...methodSpannerParams(sp_idx, first_method)} />
                      <td
                        rowSpan={row_span}
                        align={"center"}
                        className={"text-center"}
                        style={{ fontSize: "90%" }}
                      >
                        {sp.frequency}
                      </td>
                      <td rowSpan={row_span} className={"text-center"} style={{ fontSize: "90%" }}>
                        {sp.prefix_for_sampleid}
                      </td>
                    </tr>
                    {Object.keys(sp.methods).map((m, m_idx) => (
                      <React.Fragment key={`m${m_idx}`}>
                        {m_idx > 0 && (
                          <tr key={`m${m_idx}`}>
                            <MethodColumnSpanner {...methodSpannerParams(sp_idx, m)} />
                          </tr>
                        )}
                        {sp?.methods?.[m]?.open &&
                          sp?.methods?.[m]?.test_items
                            .sort((a, b) => (a.display_order ?? Infinity) - (b.display_order ?? Infinity))
                            .map((ti, ti_idx) => (
                              <tr key={`m${m_idx}_ti${ti_idx}`}>
                                {testItemColumns(
                                  isNSView,
                                  ti,
                                  `m${m_idx}_ti${ti_idx}`,
                                  isLargeDisplay || isNSView,
                                )}
                              </tr>
                            ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        ) : (
          <div className={"center_middle"} style={{ height: "200px" }}>
            <div align={"center"}>
              <i className={"fa fa-info-circle"} style={{ fontSize: "60px" }} />
              <h4>
                <FormattedMessage
                  id={
                    !isNSView
                      ? "samplemanagement.no.sample.points.message"
                      : "samplemanagement.no.ns.recipes.message"
                  }
                  defaultMessage={
                    !isNSView ? "No Sample Points Have Been Registered" : "No NS Recipes Have Been Registered"
                  }
                />
              </h4>
            </div>
          </div>
        ))}

      {/* Edit Sample Point Modal */}
      {allowActions && showSamplePointModal && user_permissions.includes("change_samplingpoint") && (
        <ModalSamplePoint
          show={showSamplePointModal}
          onHide={() => {
            setSelectedSamplePoint(null)
            hideModal(setShowSamplePointModal)
          }}
          sampling_point={selectedSamplePoint}
          intl={intl}
        />
      )}
    </React.Fragment>
  )
}

export const testItemColumns = (
  isNSView,
  ti,
  key,
  show_ranges = false,
  show_method = false,
  edit_value = false,
  slims_method = false,
  onTIChange = () => {},
) => {
  const rowStyle = { fontSize: "90%" }
  const displayCols = isNSView
    ? ["target_value"]
    : ["min_process_range", "max_process_range", "min_test_range", "max_test_range"]

  const NumericalTD = (val, key) => (
    <td key={key} style={{ ...rowStyle, textAlign: "right" }}>
      {isNumber(val.value) ? val.value : "-"}
    </td>
  )

  let flexible_columns = []
  show_method &&
    flexible_columns.push(
      <td key={`method_${key}`} className={"text-center"} style={rowStyle}>
        {ti?.source_tag?.source?.name || "-"}
      </td>,
    )

  show_ranges &&
    flexible_columns.push(
      displayCols.map((metric) => <NumericalTD key={`${metric}_${key}`} value={ti[metric]} />),
    )

  flexible_columns.push(
    ti && edit_value ? (
      <td key={`units_${key}`} className={"text-center"} style={rowStyle}>
        <input
          type={"number"}
          onWheel={(e) => e.target.blur()}
          className={"form-control form-control-sm smp_data_input"}
          disabled={slims_method}
          value={isNumber(ti?.value) ? ti.value : ""}
          onChange={(e) => onTIChange(e, ti)}
        />
        &nbsp;&nbsp;
        {ti?.source_tag?.units || "-"}
      </td>
    ) : (
      <td key={`units_${key}`} className={"text-center"} style={rowStyle}>
        {ti?.source_tag?.units || "-"}
      </td>
    ),
  )

  return [
    <td key={`tag_${key}`} className={"text-center"} style={rowStyle}>
      {ti?.source_tag?.tag?.name || "-"}
    </td>,
    <td key={`label_${key}`} className={"text-center"} style={rowStyle}>
      {ti?.label || "-"}
    </td>,
    ...flexible_columns,
  ]
}

export const getRowSpanNum = (sp) => {
  const methods = Object.keys(sp.methods) || []
  const method_length = methods.length || 1
  const ti_rows =
    methods.length > 0
      ? methods
          .filter((m) => sp.methods[m].open)
          .map((m) => sp.methods[m].test_items.length)
          .reduce((acc, num) => acc + num, 0)
      : 0
  return method_length + ti_rows
}

export default withSampleConfig(SampleSchedule)

SampleSchedule.propTypes = {
  allowSelection: PropTypes.bool,
  allowActions: PropTypes.bool,
  focus_sample_points: PropTypes.PropTypes.array, // List of template SP objs
  largeScreenCutoff: PropTypes.number,
  sample_point_status: PropTypes.string.isRequired, // SampleConfigProvider - API_STATUS (String)
  sample_point_delete_status: PropTypes.string.isRequired, // SampleConfigProvider - API_STATUS (String)
  sample_points_selected: PropTypes.object.isRequired, // SampleConfigProvider - Object: keys are SamplingPoint ids selected when allowSelect={true}
  sample_point_data: PropTypes.array.isRequired, // SampleConfigProvider - List of all sampling point objects
  getSamplePoints: PropTypes.func.isRequired, // SampleConfigProvider - Function
  deleteSamplePoint: PropTypes.func.isRequired, // SampleConfigProvider - Function
  updateSelectedSamplePoints: PropTypes.func.isRequired, // SampleConfigProvider - Function
  user_permissions: PropTypes.array.isRequired, // SampleConfigProvider - List of all permissions current user has
}
